/**
 * Processing runs, the analysis catalogue, and the results they produce.
 */
import { z } from 'zod';
import { runStatusSchema } from './enums';

export const processingRunSchema = z.object({
	pkProcessingRunId: z.string(),
	fkFileId: z.string(),
	status: runStatusSchema,
	progress: z.number(),
	progressNote: z.string().nullable(),
	parquetKey: z.string().nullable(),
	rowCount: z.number().nullable(),
	errorMessage: z.string().nullable(),
	startedAt: z.string().nullable(),
	finishedAt: z.string().nullable(),
});

/** `SALES_ONLY | RETURNS_ONLY | BOTH` — which classification of rows an
 *  analysis can read. */
export const analysisDataTypeSchema = z.enum([
	'SALES_ONLY',
	'RETURNS_ONLY',
	'BOTH',
]);

/**
 * Effective exception state, as the API reports it.
 *
 * `NOT_IMPLEMENTED` when the analysis has no exception rule of its own yet
 * (enabling is refused); otherwise the firm's on/off setting.
 */
export const exceptionCalculationStateSchema = z.enum([
	'ENABLED',
	'DISABLED',
	'NOT_IMPLEMENTED',
]);

/** A standard field shown by its label, never its key — `{key,label}` pairs
 *  travel together everywhere a required/missing field is named. */
export const requiredColumnSchema = z.object({
	key: z.string(),
	label: z.string(),
});

export const analysisDefinitionSchema = z.object({
	slug: z.string(),
	code: z.string(),
	name: z.string(),
	primitive: z.string(),
	blurb: z.string(),
	requires: z.array(z.string()),
	/** SUMMARY: an informational table. TEST: every result row is a finding. */
	kind: z.enum(['SUMMARY', 'TEST']).catch('TEST'),
	/**
	 * Landing on the catalogue endpoint alongside this task's backend work.
	 * Optional-tolerant: a screen reading the catalogue before the server adds
	 * these must not fail to parse over fields it does not use yet.
	 */
	dataType: analysisDataTypeSchema.optional(),
	requiredColumns: z.array(requiredColumnSchema).optional(),
	exceptionCalculation: exceptionCalculationStateSchema.optional(),
});

export const resultColumnSchema = z.object({
	key: z.string(),
	label: z.string(),
	numeric: z.boolean(),
});

export const resultRowSchema = z.object({
	values: z.array(z.union([z.string(), z.number(), z.null()])),
	/** A row the analysis wants a person to look at, e.g. an (unknown) bucket. */
	flagged: z.boolean(),
});

export const analysisResultSchema = z.object({
	runId: z.string(),
	analysis: analysisDefinitionSchema,
	columns: z.array(resultColumnSchema),
	rows: z.array(resultRowSchema),
	rowsRead: z.number(),
	/** What could not be tested or grouped. Stated, never dropped. */
	note: z.string(),
});

/**
 * A run read back from the database — `GET /analysis-runs/:runId`.
 *
 * Keys are camelCase unlike the other endpoints: this one sends a Prisma row
 * almost straight to JSON.
 *
 * `rows` is one page, not the whole result — `page`/`pageSize` say which. Read
 * a cut by comparing `storedRows` against `resultRows`, never from
 * `rows.length`.
 */
export const analysisRunDetailSchema = z.object({
	pkAnalysisRunId: z.string(),
	/** The catalogue slug, not a row id. */
	analysisSlug: z.string(),
	analysis: analysisDefinitionSchema.nullable(),
	fkFileId: z.string().nullable(),
	status: runStatusSchema,
	columns: z.array(resultColumnSchema).nullable(),
	rowsRead: z.number().nullable(),
	/** What the analysis said about its own result — the basis it split sales
	 *  from returns on, what it could not test, a match rate too low to read as
	 *  a finding. Persisted with the figures it qualifies (Prisma
	 *  `AnalysisRun.note`); `""` for a run that had nothing to say and for one
	 *  from before it was stored, which read the same way.
	 *
	 *  `.catch('')` and not a bare `z.string()`: this screen renders runs the
	 *  API has been answering since before the field existed, and a result
	 *  table that refuses to parse because its caveat is absent is a worse
	 *  outcome than one rendered without it. */
	note: z.string().catch(''),
	/** The result's true total, before any cut. */
	resultRows: z.number(),
	/** Rows actually persisted for this run — `<= resultRows`. */
	storedRows: z.number(),
	/** Flagged-row count over the WHOLE stored result, not just this page. */
	flaggedRows: z.number(),
	/** Per numeric column key, its sum over the whole stored result. */
	sums: z.record(z.string(), z.number()),
	/* The one non-numeric column (if any) that reads as a small bucket set. */
	bucketing: z
		.object({
			label: z.string(),
			entries: z.array(
				z.tuple([
					z.string(),
					z.object({ rows: z.number(), flagged: z.number() }),
				]),
			),
		})
		.nullable(),
	/**
	 * OPTIONAL, and the old interface was wrong to call it required.
	 *
	 * Its own comment there admitted the truth: `analysisService.getRun` does
	 * not send this field yet, so it is `undefined` at runtime wherever that
	 * response lands. Declared as a plain `boolean`, that was a type the data
	 * had never once matched — and a strict `.parse()` would have thrown on
	 * every analysis run rather than quietly carrying the lie forward.
	 *
	 * It goes back to `z.boolean()` the day the endpoint sends it.
	 */
	inReport: z.boolean().optional(),
	errorMessage: z.string().nullable(),
	startedAt: z.string(),
	finishedAt: z.string().nullable(),
	/** 1-based. This page's rows are `rows` below. */
	page: z.number(),
	pageSize: z.number(),
	rows: z.array(resultRowSchema),
});

/**
 * The one filter shape shared by the canonical-row preview and audit
 * creation's snapshot resolution — `clientId` travels as the route param on
 * both, not as a field here.
 */
export const canonicalRowFilterSchema = z.object({
	periodStart: z.string().optional(),
	periodEnd: z.string().optional(),
	customerId: z.string().optional(),
	classification: z.string().optional(),
	materialId: z.string().optional(),
	plantCode: z.string().optional(),
	documentType: z.string().optional(),
	fileId: z.string().optional(),
	/** The "pick a group / pick files" selection — ids as strings. */
	fileIds: z.array(z.string()).optional(),
});

/**
 * `canonicalRowService.preview` — `byClassification` is keyed by whatever
 * classification strings are actually present on this client's rows
 * (including the null bucket, as `'UNCLASSIFIED'`), not a fixed vocabulary.
 */
export const canonicalRowPreviewSchema = z.object({
	rowCount: z.number(),
	byClassification: z.record(z.string(), z.number()),
});

export type ProcessingRun = z.infer<typeof processingRunSchema>;
export type AnalysisDefinition = z.infer<typeof analysisDefinitionSchema>;
export type ResultColumn = z.infer<typeof resultColumnSchema>;
export type ResultRow = z.infer<typeof resultRowSchema>;
export type AnalysisResult = z.infer<typeof analysisResultSchema>;
export type AnalysisRunDetail = z.infer<typeof analysisRunDetailSchema>;
export type CanonicalRowFilter = z.infer<typeof canonicalRowFilterSchema>;
export type CanonicalRowPreview = z.infer<typeof canonicalRowPreviewSchema>;

/** `GET /audits/:id/coverage` — what the report's cover figures come from. */
export const auditCoverageSchema = z.object({
	files: z.number(),
	rows: z.number(),
	sales: z.number(),
	returns: z.number(),
	adjustments: z.number(),
	unknown: z.number(),
	filesExcluded: z.number(),
	filesAwaiting: z.number(),
});

/** One canonical row traced back to the source row it came from. */
export const lineageResultSchema = z.object({
	fkFileId: z.string(),
	fileName: z.string(),
	sourceRowNumber: z.number(),
	values: z.record(z.string(), z.string().nullable()),
});

export type AuditCoverage = z.infer<typeof auditCoverageSchema>;
export type LineageResult = z.infer<typeof lineageResultSchema>;

/**
 * One list of analyses per audit — `GET /audits/:id/analysis-overview` —
 * whatever engine computes each one. One row per `AuditAnalysis`.
 */

export const auditAnalysisStatusSchema = z.enum([
	'NOT_RUN',
	'QUEUED',
	'RUNNING',
	'COMPLETED',
	'FAILED',
]);

export const analysisAvailabilitySchema = z.enum(['READY', 'BLOCKED']);

export const analysisOverviewRowSchema = z.object({
	slug: z.string(),
	ref: z.string(),
	name: z.string(),
	description: z.string(),
	kind: z.enum(['SUMMARY', 'TEST']),
	dataType: analysisDataTypeSchema,
	requiredColumns: z.array(requiredColumnSchema),
	/** Null = not checked yet (the audit has no data scope). */
	availability: analysisAvailabilitySchema.nullable(),
	missingColumns: z.array(requiredColumnSchema),
	blockReason: z.string().nullable(),
	status: auditAnalysisStatusSchema,
	runAt: z.string().nullable(),
	startedAt: z.string().nullable(),
	completedAt: z.string().nullable(),
	durationMs: z.number().nullable(),
	rowsAnalysed: z.number().nullable(),
	resultRows: z.number().nullable(),
	/** Null = not calculated (disabled / not implemented / legacy). */
	exceptionFindings: z.number().nullable(),
	supportingRows: z.number().nullable(),
	exceptionCalculation: exceptionCalculationStateSchema,
	raisesExceptions: z.boolean().catch(false),
	/** `status = COMPLETED AND run_at < audits.scope_locked_at`. */
	outdated: z.boolean().catch(false),
	error: z.string().nullable(),
	hasResult: z.boolean(),
	hasExcel: z.boolean(),
	excelUnavailableReason: z.string().nullable(),
});

/** One of the client's analyses and the reports that implement it, in order. */
export const clientAnalysisSchema = z.object({
	number: z.number(),
	title: z.string(),
	/** The client's own description of the rule. */
	description: z.string().catch(''),
	reports: z.array(
		z.object({ slug: z.string(), label: z.string(), sheet: z.string() }),
	),
	/** Why there is nothing to run (#11, #30, #31); null otherwise. */
	onHold: z.string().nullable(),
});

export const analysisOverviewSchema = z.object({
	rows: z.array(analysisOverviewRowSchema),
	summary: z.object({
		total: z.number(),
		completed: z.number(),
		withFindings: z.number(),
		noFindings: z.number(),
		failed: z.number(),
		blocked: z.number(),
		running: z.number(),
		queued: z.number(),
		notRun: z.number(),
		outdated: z.number(),
		withExcel: z.number(),
	}),
	locked: z.boolean(),
	/** The client's 34 analyses and the reports behind each: the list shows
	 *  one entry per analysis. */
	analyses: z.array(clientAnalysisSchema),
});

/**
 * `GET /audits/:id/analyses/:slug` — one analysis's own page.
 *
 * `technical` is sent only for `manageUsers`; everyone else gets `undefined`,
 * never a 403 — the page renders identically minus the collapsed section.
 */
export const analysisDetailResponseSchema = z.object({
	analysis: analysisOverviewRowSchema,
	note: z.string().catch(''),
	technical: z
		.object({
			engine: z.string(),
			dataKey: z.string().nullable(),
			excelKey: z.string().nullable(),
		})
		.optional(),
});

/** One page of one analysis's result — `GET /audits/:id/analyses/:slug/result`. */
export const resultPageSchema = z.object({
	columns: z.array(resultColumnSchema),
	/* `position`: the row's place in the whole result, for tracing it back. */
	rows: z.array(resultRowSchema.extend({ position: z.number() })),
	total: z.number(),
	page: z.number(),
	pageSize: z.number(),
	storedRows: z.number(),
	resultRows: z.number(),
	rowsRead: z.number().nullable(),
	sums: z.record(z.string(), z.number()),
	note: z.string(),
	lineageRunId: z.string().nullable(),
});

/**
 * `POST /audits/analysis-availability` — the wizard's "Choose analyses" step,
 * over the whole catalogue for one client + data-scope filter. The backend is
 * the one source of truth for Ready/Blocked; nothing here is re-derived.
 */
export const analysisAvailabilityResultSchema = z.object({
	slug: z.string(),
	availability: analysisAvailabilitySchema.nullable(),
	missingColumns: z.array(requiredColumnSchema),
	blockReason: z.string().nullable(),
});

export const analysisAvailabilityResponseSchema = z.object({
	rowCount: z.number(),
	analyses: z.array(analysisAvailabilityResultSchema),
});

export type AnalysisOverviewRow = z.infer<typeof analysisOverviewRowSchema>;
export type AnalysisOverview = z.infer<typeof analysisOverviewSchema>;
export type ClientAnalysis = z.infer<typeof clientAnalysisSchema>;
export type AnalysisDetailResponse = z.infer<
	typeof analysisDetailResponseSchema
>;
export type ResultPage = z.infer<typeof resultPageSchema>;
export type RequiredColumn = z.infer<typeof requiredColumnSchema>;
export type AnalysisDataType = z.infer<typeof analysisDataTypeSchema>;
export type ExceptionCalculationState = z.infer<
	typeof exceptionCalculationStateSchema
>;
export type AuditAnalysisStatus = z.infer<typeof auditAnalysisStatusSchema>;
export type AnalysisAvailability = z.infer<typeof analysisAvailabilitySchema>;
export type AnalysisAvailabilityResult = z.infer<
	typeof analysisAvailabilityResultSchema
>;
export type AnalysisAvailabilityResponse = z.infer<
	typeof analysisAvailabilityResponseSchema
>;
